import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import _each from 'lodash.foreach'
import styled from 'styled-components'

import { canEdit as canEditFn, isOwner as isOwnerFn } from '../../utils/auth'
import { pageView, event, ACTIONS, PAGES, CATEGORIES } from '../../utils/analytics-tracker'
import cachingClient from '../../utils/caching-client'

import { addRecentSong } from '../../utils/recents'
import windowStorage from '../../utils/window-storage'

import PermissionsModal from '../perms/permissions-modal'
import SongSets from '../setlists/song-sets'

import { ConfirmFocal } from '../../styled/confirm-modal'
import { FormWarning } from '../../styled/form-error'
import ControlBox from '../../styled/control-box'
import InputGroup, { SmallInputGroup } from '../../styled/input-group'
import Loader from '../../styled/loader'
import Meta from '../../styled/song-meta'
import TitleGroup from '../../styled/title-group'
import Select from '../../styled/select'

import { SONG_KEYS, SIGNATURES } from '../../constants'

import confirm from '../confirmable'
import LastRehearsed from './rehearsed'
import Media from './media'
import Metronome from '../metronome/custom'
import SetlistNavigation from './setlist-navigation'
import SongContent from './content'
import SongTags from '../tags/song-tags'
import Spotify from './launchSpotify'
import Transpose from './transpose'
import UserNote from './note'
import SongMeta from './song-meta'
import RehearsalFollowerBar from '../rehearse/follower-bar'
import VersionTabs from './version-tabs'

import { axios, context as AuthContext } from '../../context/auth'
import { context as RehearsalContext } from '../../context/rehearsal'

const Content = styled.div`
  width: 100%;
  @media(min-width: 1024px) {
    display: flex;
  }
`

const Song = styled.div`
  display: inline-block;
  width: 100%;
  @media(min-width: 1024px) {
    width: 65%;
  }
  padding-bottom: 25px;
`

const Features = styled.div`
  display: inline-block;  
  width: 100%;
  @media(min-width: 1024px) {
    border-left: 5px solid ${({ theme }) => theme.borderColor};
    width: 35%;
  }
`

const ErrorMessage = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: ${({ theme }) => theme.error || '#d32f2f'};
  font-size: 1.1rem;
`

const objectDisplay = obj => obj.title || obj.email

const confirmMessage = (removeObj) => {
  return (
    <div>Are you sure you want delete the song
      <ConfirmFocal>{objectDisplay(removeObj)}</ConfirmFocal>
      ?
    </div>
  )
}

let debounce
let preferenceDebounce

// Helper functions for version preference management
const getLocalVersionPreference = (songid) => {
  try {
    const key = `song-version-pref-${songid}`
    const stored = windowStorage.getItem(key)
    return stored ? JSON.parse(stored) : null
  } catch (err) {
    console.error('Error reading version preference from localStorage:', err)
    return null
  }
}

const setLocalVersionPreference = (songid, versionId) => {
  try {
    const key = `song-version-pref-${songid}`
    windowStorage.setItem(key, JSON.stringify({ versionId }))
  } catch (err) {
    console.error('Error saving version preference to localStorage:', err)
  }
}

const syncVersionPreferenceToBackend = async (songid, versionId) => {
  try {
    await axios({
      method: 'PUT',
      url: `/api/user-settings/song-version-preference/${songid}`,
      data: { versionId }
    })
  } catch (err) {
    console.error('Error syncing version preference to backend:', err)
  }
}

// let songSource = axios.CancelToken.source()
// let setlistSource = axios.CancelToken.source()

const ViewSong = ({ songid: propSongId, setlistid: propSetlistId }) => {
  const navigate = useNavigate()
  const params = useParams()

  // Use props if provided (from RehearsalView), otherwise use URL params
  const songid = propSongId || params.songid
  const setlistid = propSetlistId || params.setlistid

  const { user, loading: authLoading } = useContext(AuthContext)
  const { isOwner: isRehearsalOwner, isFollowing, setContext } = useContext(RehearsalContext)

  // Redirect to login if not authenticated (but wait for auth to finish loading)
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login')
    }
  }, [user, authLoading, navigate])

  const [changesLabel, setChangesLabel] = useState('')
  const [loading, setLoading] = useState(true)
  const [savingTransposed, setSavingTransposed] = useState(false)
  const [controlBoxClosed, setControlBoxClosed] = useState(true)
  const [songTheme, setSongTheme] = useState('inline')
  const [changed, setChanged] = useState({})
  const [song, setSong] = useState({})
  const [setlist, setSetlist] = useState()
  const [permissionError, setPermissionError] = useState(false)
  const [activeVersionId, setActiveVersionId] = useState(null)
  const [currentContent, setCurrentContent] = useState('')
  const scrollPositionRef = useRef(0)

  const [transposeSteps, setTransposeSteps] = useState(0)
  const [showPermissionsModal, setShowPermissionsModal] = useState(false)
  const canEdit = canEditFn(song)
  const isOwner = isOwnerFn(song)

  const transposeUp = () => {
    setTransposeSteps(transposeSteps === 11 ? 0 : transposeSteps + 1)
  }

  const transposeDown = () => {
    setTransposeSteps(transposeSteps === -11 ? 0 : transposeSteps - 1)
  }

  // Helper function to construct full versions array (Original + additional)
  const getAllVersions = useCallback((songData) => {
    if (!songData) return []
    return [
      {
        versionId: 'original',
        label: 'Original',
        content: songData.content,
        isOriginal: true,
        createdBy: songData.creator
      },
      ...(songData.additionalVersions || [])
    ]
  }, [])

  const handleVersionChange = useCallback((versionId) => {
    // Preserve scroll position
    const currentScroll = window.scrollY
    scrollPositionRef.current = currentScroll

    // Find the version
    const allVersions = getAllVersions(song)
    const version = allVersions.find(v => v.versionId === versionId)
    if (!version) {
      console.error('Version not found:', versionId)
      return
    }

    // Update state
    setActiveVersionId(versionId)
    setCurrentContent(version.content)

    // Update localStorage immediately
    setLocalVersionPreference(songid, versionId)

    // Debounced sync to backend
    clearTimeout(preferenceDebounce)
    preferenceDebounce = setTimeout(() => {
      if (user) {
        syncVersionPreferenceToBackend(songid, versionId)
      }
    }, 1000)

    // Restore scroll position after React updates
    setTimeout(() => {
      window.scrollTo(0, currentScroll)
    }, 0)
  }, [song, songid, user, getAllVersions])

  const refreshSong = useCallback(async (updatedSong) => {
    if (updatedSong) {
      setSong(updatedSong.data)
      setChanged({})
      // Initialize version state when song is updated
      const allVersions = getAllVersions(updatedSong.data)
      if (allVersions.length > 0) {
        const originalVersion = allVersions[0] // First is always original
        setActiveVersionId(originalVersion.versionId)
        setCurrentContent(originalVersion.content)
      }
      return
    }
    setLoading(true)
    setPermissionError(false)
    // if (songSource) {
    //   songSource.cancel('Cancel song fetch, song view unmounted')
    //   songSource = axios.CancelToken.source()
    // }
    try {
      const response = await cachingClient({
        key: 'song',
        id: songid,
        method: 'GET',
        url: `/api/songs/${songid}`,
        cancelToken: '' // songSource.token
      })
      setSong(response.data)
      addRecentSong(songid, response.data.title)
      setChanged({})
      setTransposeSteps(response.data.transposed)

      // Initialize version state
      const allVersions = getAllVersions(response.data)
      if (allVersions.length > 0) {
        // Try to load user's preferred version
        const localPref = getLocalVersionPreference(songid)
        const preferredVersionId = localPref?.versionId

        // Fetch preference from backend asynchronously
        if (user) {
          axios({
            method: 'GET',
            url: `/api/user-settings/song-version-preference/${songid}`
          }).then(prefResponse => {
            if (prefResponse.data.data.preference?.versionId) {
              const backendVersionId = prefResponse.data.data.preference.versionId
              // Check if this version still exists
              const versionExists = allVersions.some(v => v.versionId === backendVersionId)
              if (versionExists && backendVersionId !== activeVersionId) {
                // Update to backend preference if different
                setActiveVersionId(backendVersionId)
                const version = allVersions.find(v => v.versionId === backendVersionId)
                if (version) {
                  setCurrentContent(version.content)
                  setLocalVersionPreference(songid, backendVersionId)
                }
              }
            }
          }).catch(err => {
            console.error('Error fetching version preference from backend:', err)
          })
        }

        // Check if preferred version exists, otherwise fall back to original
        let versionToLoad
        if (preferredVersionId) {
          versionToLoad = allVersions.find(v => v.versionId === preferredVersionId)
        }

        // Fall back to original if preferred version doesn't exist
        if (!versionToLoad) {
          versionToLoad = allVersions[0] // First is always original
          // Update preference to original if we fell back
          if (preferredVersionId && preferredVersionId !== versionToLoad.versionId) {
            setLocalVersionPreference(songid, versionToLoad.versionId)
            if (user) {
              syncVersionPreferenceToBackend(songid, versionToLoad.versionId)
            }
          }
        }

        setActiveVersionId(versionToLoad.versionId)
        setCurrentContent(versionToLoad.content)
      }
    } catch (err) {
      console.log(err)
      // Check if it's a permission error (403 or 404)
      if (err.response && (err.response.status === 403 || err.response.status === 404)) {
        setPermissionError(true)
      }
    }
    setLoading(false)
  }, [songid, user])

  // this needs to be in a context
  const refreshSetList = useCallback(async () => {
    // if (setlistSource) {
    //   setlistSource.cancel('Cancel setlist fetch, song view unmounted')
    //   setlistSource = axios.CancelToken.source()
    // }
    if (setlistid) {
      try {
        const response = await cachingClient({
          key: 'setlist',
          id: setlistid,
          method: 'GET',
          url: `/api/setlists/${setlistid}`,
          cancelToken: '' // setlistSource.token
        })
        setSetlist(response.data)
      } catch (err) {
        console.log(err)
      }
    }
  }, [setlistid])

  useEffect(() => {
    refreshSetList()
  }, [setlistid])

  useEffect(() => {
    // Only fetch song after auth is complete and user is available
    if (!authLoading && user) {
      refreshSong()
    }
  }, [songid, authLoading, user, refreshSong])

  useEffect(() => {
    pageView(PAGES.SONG_VIEW)
    return () => {
      // if (setlistSource) {
      //   setlistSource.cancel('Cancel setlist fetch, song view unmounted')
      // }
      // if (songSource) {
      //   songSource.cancel('Cancel song fetch, song view unmounted')
      // }
    }
  }, [])

  // Update rehearsal context when owner views a song
  useEffect(() => {
    if (isRehearsalOwner && songid) {
      setContext(songid, setlistid || null)
    }
  }, [isRehearsalOwner, songid, setlistid])

  // Preserve scroll position during updates
  useEffect(() => {
    const handleScroll = () => {
      scrollPositionRef.current = window.scrollY
    }

    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  // Restore scroll position after content updates
  useEffect(() => {
    if (scrollPositionRef.current > 0) {
      window.scrollTo(0, scrollPositionRef.current)
    }
  }, [song])

  const saveChanges = async (changedData) => {
    setChangesLabel('Saving...')
    try {
      const response = await cachingClient({
        key: 'song',
        id: song._id,
        method: 'PUT',
        url: `/api/songs/${song._id}`,
        data: {
          ...song,
          ...changedData
        }
      })

      setSong(response.data)
      setChangesLabel('Saved!')
      setTimeout(() => {
        setChangesLabel('')
      }, 2000)
    } catch (err) {
      console.log(err)
    }
  }

  useEffect(() => {
    if (JSON.stringify(changed) !== '{}') {
      clearTimeout(debounce)
      debounce = setTimeout(() => {
        saveChanges(changed)
      }, 1000)
    }
  }, [changed])

  const controlsChange = (e) => {
    setChanged({
      ...changed,
      [e.target.name]: e.target.value
    })
    setSong({
      ...song,
      [e.target.name]: e.target.value
    })
    setChangesLabel('Changes...')
  }

  const saveTransposedFn = useCallback(async () => {
    setSavingTransposed(true)
    try {
      const response = await cachingClient({
        key: 'song',
        id: song._id,
        method: 'PUT',
        url: `/api/songs/${song._id}`,
        data: {
          ...song,
          transposed: transposeSteps
        }

      })

      setSong(response.data)
    } catch (err) {
      console.log(err)
    }
    setSavingTransposed(false)
  }, [transposeSteps])

  const clickEdit = (to) => {
    navigate(to)
  }

  const deleteSong = useCallback(() => {
    confirm(confirmMessage(song)).then(
      (result) => {
        axios({
          url: `/api/songs/${song._id}`,
          method: 'DELETE'
        }).then(() => {
          event(CATEGORIES.SONG, ACTIONS.SONG_DELETE)
          navigate('/songs')
        })
      },
      (result) => {
        event(CATEGORIES.SONG, ACTIONS.SONG_DELETE_CANCEL)
      }
    )
  })

  // Show permission error first (before checking loading or song validity)
  if (permissionError) {
    return (
      <div className='SongView'>
        {isFollowing && <RehearsalFollowerBar />}
        <ErrorMessage>
          You don't have permission to view this song. The rehearsal owner has navigated to content you cannot access.
        </ErrorMessage>
      </div>
    )
  }

  // Show loading state
  if (loading) {
    return (
      <div className='SongView'>
        {isFollowing && <RehearsalFollowerBar />}
        <Loader />
      </div>
    )
  }

  if (!song || !song._id) {
    return <Loader />
  }

  let nextSong
  let prevSong

  if (setlist) {
    let idx = 0
    _each(setlist.songs, setListSong => {
      if (setListSong._id === songid) {
        prevSong = setlist.songs[idx - 1]
        nextSong = setlist.songs[idx + 1]
      }
      idx++
    })
  }

  console.log('songid', songid)

  // Show loading while auth is in progress
  if (authLoading) {
    return <Loader />
  }

  console.log('song', song)
  return (
    <div className='SongView'>
      <RehearsalFollowerBar />
      {setlist && !isFollowing && <SetlistNavigation setlist={setlist} nextSong={nextSong} prevSong={prevSong} />}
      {loading && <Loader />}
      {song.cached && <FormWarning>Connection out of tune: Song read from cache</FormWarning>}
      <TitleGroup
        title={song.title}
        subtitle={song.artist}
        onEdit={canEdit && (() => clickEdit(`/songs/${songid}/edit`))}
        onPermissions={() => setShowPermissionsModal(true)}
        onDelete={isOwner && deleteSong}
      />
      {/* <SongInfoHeader>
        <h1>{song.title}
          {canEdit && <EditButton onClick={} />}
          {isOwner && <DeleteButton onClick={deleteSong} />}
        </h1>
        <h2>{song.artist}</h2>
      </SongInfoHeader> */}
      {song && user && <Metronome user={user} bpm={song.tempo} signature={song.signature} />}
      <ControlBox closed={controlBoxClosed}>
        <div className='control-box-header' onClick={() => setControlBoxClosed(!controlBoxClosed)}>Song Controls</div>
        <div className='control-box-body'>
          <InputGroup>
            <label>Transpose{song.transposed !== 0 ? 'd' : ''}</label>
            <Transpose
              song={song}
              value={transposeSteps}
              upFn={transposeUp}
              downFn={transposeDown}
              saveTransposedFn={saveTransposedFn}
              saving={savingTransposed}
            />
          </InputGroup>
          <InputGroup>
            <label>Theme</label>
            <Select
              value={{ value: songTheme, label: songTheme === 'inline' ? 'Inline' : songTheme === 'above' ? 'Chords Above' : songTheme === 'lyrics-only' ? 'Lyrics Only' : 'Chords Float Right' }}
              onChange={(option) => setSongTheme(option.value)}
              options={[
                { value: 'inline', label: 'Inline' },
                { value: 'above', label: 'Chords Above' },
                { value: 'lyrics-only', label: 'Lyrics Only' },
                { value: 'float-right', label: 'Chords Float Right' }
              ]}
            />
          </InputGroup>
        </div>
        {canEdit && (
          <div className='control-box-body'>
            <div style={{ clear: 'both' }} />
            <SmallInputGroup>
              <div className='small'>
                <label htmlFor='key'>Key: </label>
                <Select
                  value={song.key ? { value: song.key, label: song.key } : null}
                  onChange={(option) => {
                    const syntheticEvent = { target: { name: 'key', value: option ? option.value : '' } }
                    controlsChange(syntheticEvent)
                  }}
                  options={SONG_KEYS.map(songKey => ({ value: songKey, label: songKey }))}
                  isClearable
                />
              </div>
              <div className='small'>
                <label htmlFor='tempo'>Tempo: </label>
                <input
                  type='text'
                  name='tempo'
                  value={song.tempo}
                  onChange={controlsChange}
                />
              </div>
              <div className='small'>
                <label htmlFor='signature'>Signature: </label>
                <Select
                  value={song.signature ? SIGNATURES.find(sig => sig.value === song.signature) ? { value: song.signature, label: SIGNATURES.find(sig => sig.value === song.signature).display } : null : null}
                  onChange={(option) => {
                    const syntheticEvent = { target: { name: 'signature', value: option ? option.value : '' } }
                    controlsChange(syntheticEvent)
                  }}
                  options={SIGNATURES.map(sig => ({ value: sig.value, label: sig.display }))}
                  isClearable
                />
              </div>
              <div className='small'>
                <label htmlFor='duration'>Duration: </label>
                <input
                  type='text'
                  name='duration'
                  value={song.duration}
                  onChange={controlsChange}
                />
              </div>
            </SmallInputGroup>
            {changesLabel}
          </div>
        )}

      </ControlBox>

      <Content>
        <Song>
          {song && song.key && <Meta>Key: {song.key}</Meta>}
          {song && song.tempo && <Meta>Tempo: {song.tempo}</Meta>}
          <VersionTabs
            versions={getAllVersions(song)}
            activeVersionId={activeVersionId}
            onVersionChange={handleVersionChange}
          />
          <SongContent
            songTheme={songTheme}
            content={currentContent || song.content}
            transposeSteps={transposeSteps}
            saveTransposedFn={saveTransposedFn}
          />
        </Song>
        <Features>
          <UserNote song={song} />
          <SongMeta song={song} />
          <Media link={song.media} />
          <Spotify song={song} />
          {canEdit && <LastRehearsed lastRehearsed={song.lastRehearsed} songid={songid} refreshSong={refreshSong} />}
          {canEdit && <SongSets songid={songid} />}
          <SongTags canEdit={canEdit} songid={songid} />
        </Features>
      </Content>

      {/* Permissions Modal */}
      <PermissionsModal
        isOpen={showPermissionsModal}
        onClose={() => setShowPermissionsModal(false)}
        contentType='song'
        contentId={songid}
        contentName={song.title}
      />
    </div>
  )
}

export default ViewSong
